/**
 * Canadian French locale for webhooks-ui.
 * Export is the "webhooks" subtree: host apps merge this into their i18n
 * resources as translation.webhooks so that t('webhooks.list.integrated') etc. resolve.
 */
import type { WebhooksLocale } from './en'

export const webhooksFrCa = {
  common: {
    createIntegration: `Créer une intégration`,
    updateIntegration: `Mettre à jour l'intégration`,
    failedToSave: `Échec de l'enregistrement`,
    learnMore: `en savoir plus`,
    beta: `Bêta`,
  },
  list: {
    failedToLoad: `Échec du chargement des intégrations`,
    noIntegrationsAvailable: `Aucune intégration disponible`,
    integrated: `Intégré`,
    learnMore: `En savoir plus`,
  },
  detail: {
    status: `Statut`,
    on: `Activé`,
    off: `Désactivé`,
    failedToLoad: `Échec du chargement de l'intégration`,
    back: `← Retour`,
    webhooksV2Title: `Webhooks v2.0`,
    xlDentTitle: `Intégration pour Screen pops`,
    integrationTitle: `Intégration pour {{name}}`,
    webhooksV2Description: `La charge utile de l'événement sera envoyée à l'URL de rappel que vous fournissez. Vous devez spécifier les événements à envoyer à l'aide des Sélecteurs d'événements`,
    xlDentDescription: `Sachez qui appelle. Gagnez du temps. Obtenez rapidement les réponses. Recevez des notifications Screen pop pour les appels entrants et sortants`,
    remoteIdLabel: `ID distant`,
    practiceGuidLabel: `GUID de cabinet`,
  },
  dolphin: {
    webhookUrl: `URL du Webhook`,
  },
  webhooksV2: {
    remoteId: `ID distant`,
    remoteIdPlaceholder: `Utilisé pour identifier le PBX source.`,
    callbackUrl: `URL de rappel`,
    callbackUrlPlaceholder: `https://yourdomain.com/mango/event-handler`,
    eventSelectors: `Sélecteurs d'événements`,
    eventSelectorsLoadError: `Les sélecteurs d'événements n'ont pas pu être chargés — actualisez pour réessayer.`,
    noEventsWarning: `Vous n'avez choisi aucun sélecteur d'événements, ce qui entraînera l'absence d'envoi d'événements à cette URL. Enregistrer quand même?`,
    goBack: `Retour`,
    saveAnyway: `Enregistrer quand même`,
  },
  xlDent: {
    tableDescription: `Associez les extensions Mango Voice à l'adresse IP de la station de travail.`,
    extensionName: `Nom de l'extension`,
    extensionNumber: `N° d'extension`,
    workstationIp: `Adresse IP de la station de travail`,
    enableDisable: `Activer/Désactiver`,
    disabled: `Désactivé.`,
    ipPlaceholder: `e.g. 192.168.1.1:8888`,
    update: `Mettre à jour`,
    devicesNote: `Remarque : Tout changement nécessitera de mettre à jour l'appareil dans « Mes appareils » pour que les modifications prennent effet.`,
  },
  // Le texte de cette section est intentionnellement aligné avec apidocs.mangovoice.com
  // afin d'assurer la cohérence du langage entre le portail et la documentation de l'API.
  authKeys: {
    title: `Clés d'autorisation`,
    description: `Générez un jeton en trois parties qui authentifie les requêtes aux APIs mondiales de Mango Voice. Partagez-le avec votre partenaire d'intégration pour qu'il puisse effectuer des appels API en votre nom.`,
    confidential: `Les jetons doivent rester confidentiels. Exposer les jetons au public peut entraîner le vol, la perte ou la corruption de données.`,
    noKeys: `Aucune clé d'autorisation n'a encore été générée.`,
    generate: `Générer`,
    regenerate: `Régénérer`,
    remove: `Supprimer`,
    copy: `Copier`,
    copied: `Copié !`,
    apiDocsLabel: `Voir la documentation de l'API`,
    failedToLoad: `Échec du chargement des clés d'autorisation`,
    failedToGenerate: `Échec de la génération des clés d'autorisation`,
    failedToRegenerate: `Échec de la régénération des clés d'autorisation`,
    failedToRemove: `Échec de la suppression des clés d'autorisation`,
    confirmRegenerateTitle: `Régénérer les clés d'autorisation`,
    confirmRegenerateBody: `La régénération des clés d'autorisation peut désactiver les intégrations existantes. Êtes-vous sûr de vouloir continuer ?`,
    confirmRemoveTitle: `Supprimer les clés d'autorisation`,
    confirmRemoveBody: `La suppression des clés d'autorisation peut désactiver les intégrations existantes. Êtes-vous sûr de vouloir continuer ?`,
    confirmCancel: `Annuler`,
    confirmProceed: `Continuer`,
    combinedToken: `Jeton combiné`,
    combinedTokenHint: `Utilisé comme variables de chemin dans les requêtes API`,
    formatIdent1: `ident1`,
    formatIdent2: `ident2`,
    formatIdent3: `ident3`,
  },
  admin: {
    apiAccess: {
      title: `Accès à l'API`,
      selectPbx: `Sélectionner un PBX`,
      selectCompanyFirst: `Sélectionnez d'abord une entreprise`,
      noPbxSelected: `Sélectionnez une entreprise et un PBX ci-dessus pour gérer les clés d'autorisation.`,
    },
    // admin.list is an internal admin3 namespace — English strings are intentional
    list: {
      title: `Webhook Integrations`,
      description: `View and manage webhook integration status across all PBXes for a company`,
      companyLabel: `Company`,
      companyPlaceholder: `Select a company…`,
      pbxFilter: `PBX`,
      allPbxes: `All PBXes`,
      integrationFilter: `Integration`,
      allIntegrations: `All Integrations`,
      noCompanySelected: `Select a company to view integrations`,
      noIntegrationsFound: `No integrations found`,
      columnStatus: `Status`,
      columnActive: `Active`,
      notIntegrated: `Not integrated`,
    },
  },
} satisfies WebhooksLocale
